import { PAP_Add_DashShoppingCart } from "@mirage/analytics/events/types/add_dash_shopping_cart";
import { PAP_Empty_DashShoppingCart } from "@mirage/analytics/events/types/empty_dash_shopping_cart";
import { PAP_Remove_DashShoppingCart } from "@mirage/analytics/events/types/remove_dash_shopping_cart";
import { ChatSourceMirageSearchResult } from "@mirage/mosaics/Chat/types";
import { reportPapEvent } from "@mirage/service-product-logging";
import { atom, useAtom } from "jotai";
import { useCallback, useEffect } from "react";

export const searchResultsShoppingCartAtom = atom<
  Set<ChatSourceMirageSearchResult>
>(new Set<ChatSourceMirageSearchResult>());
export const isShoppingCardActivated = atom<boolean>(false);
export const curateTooltipShownAtom = atom<boolean>(false);

export const useResultsShoppingCart = () => {
  const [cartItems, setCartItems] = useAtom(searchResultsShoppingCartAtom);
  const [isActivated, setIsActivated] = useAtom(isShoppingCardActivated);
  const [isCurateTooltipShown, setIsCurateTooltipShown] = useAtom(
    curateTooltipShownAtom,
  );

  const addItemToCart = useCallback(
    (item: ChatSourceMirageSearchResult) => {
      setCartItems((prev) => {
        const newCartItems = new Set(prev);
        newCartItems.add(item);
        return newCartItems;
      });
      reportPapEvent(
        PAP_Add_DashShoppingCart({
          resultUuid: item.searchResult.uuid,
        }),
      );
    },
    [setCartItems, reportPapEvent],
  );

  const removeItemFromCart = useCallback(
    (uuid: string) => {
      setCartItems((prev) => {
        const newCartItems = new Set(prev);
        newCartItems.forEach((item) => {
          if (item.searchResult.uuid === uuid) {
            newCartItems.delete(item);
          }
        });
        return newCartItems;
      });
      reportPapEvent(
        PAP_Remove_DashShoppingCart({
          resultUuid: uuid,
        }),
      );
    },
    [setCartItems, reportPapEvent],
  );

  const clearShoppingCart = useCallback(
    (ignoreLogging: boolean = false) => {
      setCartItems(new Set<ChatSourceMirageSearchResult>());
      if (!ignoreLogging) {
        reportPapEvent(PAP_Empty_DashShoppingCart());
      }
    },
    [setCartItems, reportPapEvent],
  );

  useEffect(() => {
    // If the cart is deactivated, clear the items
    if (!isActivated) {
      setCartItems(new Set<ChatSourceMirageSearchResult>());
    }
  }, [isActivated]);

  return {
    cartItems,
    addItemToCart,
    removeItemFromCart,
    clearShoppingCart,
    isActivated,
    setIsActivated,
    isCurateTooltipShown,
    setIsCurateTooltipShown,
  };
};
